/**
 * Players — list, ban/unban, set password, add credits, delete.
 *
 * `ban` also deletes every refresh token, `password` additionally invalidates
 * outstanding reset links, `add-credits` goes through `EconomyService` so the
 * ledger records it, and `DELETE` cascades the player's whole footprint. All
 * five are here, and the three destructive ones go through a confirmation.
 *
 * ─── Repainting, and the search box ─────────────────────────────────────────
 * The header is rendered ONCE and only the body is repainted. That is not a
 * performance choice: the search field lives in the header, and rewriting it
 * under a half-typed query would throw the query away and move the caret —
 * exactly the trap documented at the top of `settings-ui.ts`.
 */
import {
  banUser, createUser, deleteUser, getUser, grantCredits, impersonateUser, listUserEvents, listUsers,
  setUserPassword, unbanUser, updatePlayer, verifyUserEmail, verifyUserKyc,
} from '../admin-api';
import type { PlayerRole } from '../admin-api';
import type {
  AdminUserDetail, AdminUserRow, CurrencyCode, Paged, PlayerEvent, UsersQuery,
} from '../admin-api.types';
import {
  badge, checkbox, errorText, esc, field, fmtDate, pager, readForm, section, table,
} from '../admin-kit';
import type { AdminTab } from '../admin-kit';

/** Columns whose values are secrets or noise in a detail drawer. */
const HIDDEN_DETAIL_COLUMNS = new Set(['password', 'id', '_count']);

const ROLES: PlayerRole[] = ['PLAYER', 'ADMIN', 'KYC_ADMIN'];

function roleSelect(current: string): string {
  return `<label class="tg-adm-field">
    <span class="tg-adm-label">Role</span>
    <select name="role">${ROLES.map((r) => `<option value="${r}"${r === current ? ' selected' : ''}>${r}</option>`).join('')}</select>
  </label>`;
}

/** One per drawer paint: a double-click resubmits the SAME form, hence the same key. */
function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  return typeof c?.randomUUID === 'function'
    ? c.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function createForm(): string {
  return `
    <div class="tg-adm-drawer">
      <header class="tg-adm-head"><h2 class="tg-adm-title">New player</h2>
        <div class="tg-adm-toolbar"><button type="button" class="tg-adm-btn" data-act="cancel-create">Cancel</button></div>
      </header>
      <form class="tg-adm-form" data-form="create">
        ${field('email', 'Email', '', { type: 'email' })}
        ${field('username', 'Username', '', { hint: 'Optional — generated from the email when empty.' })}
        ${field('password', 'Password', '', { type: 'password', hint: 'At least 8 characters.' })}
        ${roleSelect('PLAYER')}
        ${checkbox('emailVerified', 'Mark email verified', true)}
        <div class="tg-adm-actions"><button type="submit" class="tg-adm-btn tg-adm-btn-primary">Create player</button></div>
      </form>
    </div>`;
}

function statusBadges(u: AdminUserRow): string {
  const out = [u.banned ? badge('banned', 'bad') : badge('active', 'ok')];
  if (!u.emailVerified) out.push(badge('unverified', 'warn'));
  if (u.kycStatus && u.kycStatus !== 'NONE') {
    out.push(badge(u.kycStatus.toLowerCase(), u.kycStatus === 'APPROVED' ? 'ok' : 'warn'));
  }
  if (u.role !== 'PLAYER') out.push(badge(u.role.toLowerCase(), 'mute'));
  return out.join(' ');
}

/**
 * The per-row buttons.
 *
 * The two verify actions are drawn only where they would DO something — an
 * account that is already verified gets no button rather than a disabled one.
 * That keeps the common row (a verified player) to two buttons, and makes the
 * presence of a third the signal that something needs attention.
 */
function rowActions(u: AdminUserRow): string {
  const verifyEmail = u.emailVerified ? '' : `
      <button type="button" class="tg-adm-btn" data-act="verify-email" data-id="${esc(u.id)}"
        title="Mark this address verified without the emailed link">Verify email</button>`;
  const verifyKyc = u.kycStatus === 'APPROVED' ? '' : `
      <button type="button" class="tg-adm-btn" data-act="verify-kyc" data-id="${esc(u.id)}"
        data-pending="${u.kycStatus === 'PENDING'}"
        title="Approve identity verification">Verify KYC</button>`;
  return `
    <div class="tg-adm-rowacts">
      <button type="button" class="tg-adm-btn" data-act="view" data-id="${esc(u.id)}">Open</button>
      <button type="button" class="tg-adm-btn" data-act="${u.banned ? 'unban' : 'ban'}"
        data-id="${esc(u.id)}">${u.banned ? 'Unban' : 'Ban'}</button>
      ${verifyEmail}${verifyKyc}
    </div>`;
}

/**
 * The detail drawer.
 *
 * The key/value block iterates whatever `GET /admin/users/:id` returned rather
 * than naming columns: the User model is extended per game (this one adds a
 * character, a hideout, a home), and a hand-written list would quietly stop
 * showing new columns the moment the schema grew.
 */
function drawer(user: AdminUserDetail, events: PlayerEvent[], eventsError: string): string {
  const rows = Object.entries(user)
    .filter(([key, value]) => !HIDDEN_DETAIL_COLUMNS.has(key) && typeof value !== 'object')
    .map(([key, value]) => `<div><span>${esc(key)}</span><span>${esc(value)}</span></div>`)
    .join('');

  const eventList = eventsError
    ? `<p class="tg-adm-empty">${esc(eventsError)}</p>`
    : table(
      events,
      [
        { label: 'When', cell: (e) => esc(fmtDate(e.eventAt)), tight: true },
        { label: 'Type', cell: (e) => `<span class="tg-adm-mono">${esc(e.eventType)}</span>`, tight: true },
        { label: 'What', cell: (e) => `<strong>${esc(e.title)}</strong><br>${esc(e.description)}` },
        { label: 'From', cell: (e) => esc([e.ipAddress, e.geo].filter(Boolean).join(' · ') || '—'), tight: true },
      ],
      'Nothing recorded for this account yet.',
    );

  return `
    <div class="tg-adm-drawer">
      <header class="tg-adm-head">
        <div>
          <h2 class="tg-adm-title">${esc(user.username || user.email)}</h2>
          <p class="tg-adm-sub tg-adm-mono">${esc(user.id)}</p>
        </div>
        <div class="tg-adm-toolbar">
          <button type="button" class="tg-adm-btn" data-act="impersonate" data-id="${esc(user.id)}"
            title="Sign this browser in as the player for testing">Open as player</button>
          <button type="button" class="tg-adm-btn" data-act="close-detail">Close</button>
        </div>
      </header>

      <div class="tg-adm-kv">${rows}</div>

      <div class="tg-adm-form-row tg-adm-form-row-top" style="margin-top:16px">
        <form class="tg-adm-form" data-form="edit" style="flex:1 1 300px">
          ${field('username', 'Username', user.username ?? '')}
          ${field('email', 'Email', user.email, { type: 'email', hint: 'Changing it marks the address unverified.' })}
          ${roleSelect(user.role)}
          <div class="tg-adm-actions"><button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save changes</button></div>
        </form>

        <form class="tg-adm-form" data-form="credits" style="flex:1 1 300px">
          <input type="hidden" name="idempotencyKey" value="${esc(newIdempotencyKey())}">
          ${field('amount', 'Grant currency', '', { type: 'number', placeholder: '100' })}
          <label class="tg-adm-field">
            <span class="tg-adm-label">Currency</span>
            <select name="currency">
              <option value="SOFT">Cash (soft)</option>
              <option value="PREMIUM">Gold (premium)</option>
            </select>
          </label>
          ${field('reason', 'Reason', '', { placeholder: 'Compensation for…', hint: 'Required. Written to the ledger and the activity log.' })}
          <div class="tg-adm-actions">
            <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Grant</button>
          </div>
        </form>

        <form class="tg-adm-form" data-form="password" style="flex:1 1 300px">
          ${field('password', 'Set a new password', '', {
    type: 'password',
    hint: 'At least 8 characters. Signs the player out of every session.',
  })}
          <div class="tg-adm-actions">
            <button type="submit" class="tg-adm-btn">Set password</button>
            <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-act="delete">Delete account</button>
          </div>
        </form>
      </div>

      <h3 class="tg-adm-label" style="margin-top:18px">Recent activity</h3>
      ${eventList}
    </div>`;
}

export const usersTab: AdminTab = {
  id: 'users',
  label: 'Players',
  group: 'Players',

  mount(host, ctx) {
    let live = true;
    let query: UsersQuery = { page: 1, status: 'all' };
    let page: Paged<AdminUserRow> | null = null;
    let error = '';
    let detail: AdminUserDetail | null = null;
    let events: PlayerEvent[] = [];
    let eventsError = '';
    let creating = false;

    host.innerHTML = section(
      'Players',
      'Search, inspect, and act on accounts. Every action here is recorded server-side.',
      '<p class="tg-adm-empty">Loading…</p>',
      `<input class="tg-adm-search" data-search placeholder="Search name or email" style="min-width:220px">
       <select data-status>
         <option value="all">All</option>
         <option value="active">Active</option>
         <option value="banned">Banned</option>
       </select>
       <button type="button" class="tg-adm-btn" data-act="reload">Search</button>
       <button type="button" class="tg-adm-btn tg-adm-btn-primary" data-act="new-user">New player</button>`,
    );
    const body = host.querySelector<HTMLElement>('.tg-adm-body')!;
    const searchInput = host.querySelector<HTMLInputElement>('[data-search]')!;
    const statusSelect = host.querySelector<HTMLSelectElement>('[data-status]')!;

    function paint(): void {
      if (error) { body.innerHTML = `<p class="tg-adm-empty">${esc(error)}</p>`; return; }
      if (!page) { body.innerHTML = '<p class="tg-adm-empty">Loading…</p>'; return; }

      // The drawer goes ABOVE the list, not below it. Appended after a
      // twenty-row table it opened entirely below the fold, so clicking Open
      // looked like it did nothing at all — the request had fired, the drawer
      // had rendered, and none of it was on screen.
      body.innerHTML = `
        ${creating ? createForm() : ''}
        ${detail ? drawer(detail, events, eventsError) : ''}
        ${table(
    page.items,
    [
      {
        label: 'Player',
        cell: (u) => `<strong>${esc(u.username || '—')}</strong><br>
          <span class="tg-adm-mono">${esc(u.email)}</span>`,
      },
      { label: 'Status', cell: statusBadges, tight: true },
      { label: 'Level', cell: (u) => esc(u.level), tight: true },
      {
        label: 'Balances',
        cell: (u) => `<span class="tg-adm-bal">${esc(u.softBalance)} <em>cash</em></span>
          <span class="tg-adm-bal">${esc(u.premiumBalance)} <em>gold</em></span>`,
        width: '140px',
      },
      { label: 'Joined', cell: (u) => esc(fmtDate(u.createdAt)), tight: true },
      { label: 'Last seen', cell: (u) => esc(fmtDate(u.lastSeenAt)), tight: true },
      { label: '', cell: rowActions, tight: true },
    ],
    'No accounts match that search.',
  )}
        ${pager(page.page, page.pageSize, page.total)}`;
    }

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listUsers(query);
        if (!live) return;
        page = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function openDetail(id: string): Promise<void> {
      eventsError = '';
      try {
        detail = await getUser(id);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
        return;
      }
      if (!live) return;
      paint();
      // The audit trail is a second request and a second failure mode — a user
      // whose events cannot be read is still a user worth showing, so this
      // resolves into the already-painted drawer instead of blocking it.
      try {
        const eventPage = await listUserEvents(id);
        if (!live) return;
        events = eventPage.data;
      } catch (err) {
        if (!live) return;
        events = [];
        eventsError = errorText(err);
      }
      paint();
    }

    /** Runs a write, reports it, and reloads both the list and any open drawer. */
    async function act(what: string, run: () => Promise<unknown>): Promise<void> {
      try {
        await run();
        if (!live) return;
        ctx.notify(what);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
        return;
      }
      await load();
      if (detail && live) await openDetail(detail.id);
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-page]');
      if (!el) return;
      const id = el.dataset.id ?? '';

      if (el.dataset.page) {
        query = { ...query, page: Number(el.dataset.page) };
        void load();
        return;
      }

      switch (el.dataset.act) {
        case 'reload':
          query = {
            page: 1,
            search: searchInput.value.trim() || undefined,
            status: statusSelect.value as UsersQuery['status'],
          };
          void load();
          break;
        case 'view':
          creating = false;
          void openDetail(id);
          break;
        case 'new-user':
          creating = true;
          detail = null;
          events = [];
          paint();
          break;
        case 'cancel-create':
          creating = false;
          paint();
          break;
        // Core mints a 60s URL that sets the player's cookies in whichever
        // browser opens it. This panel shares that browser, so opening it
        // REPLACES the admin's own session — said out loud in the dialog.
        case 'impersonate':
          ctx.confirm({
            title: 'Open the game as this player?',
            body: 'A new tab signs this browser in as the player. Your admin session in this browser is replaced, so you will need to sign back in as an administrator afterwards.',
            confirmLabel: 'Open as player',
            onConfirm: () => {
              impersonateUser(id).then((r) => {
                if (live) window.open(r.url, '_blank', 'noopener');
              }).catch((err) => { if (live) ctx.notify(errorText(err), 'err'); });
            },
          });
          break;
        case 'unban':
          void act('Account unbanned.', () => unbanUser(id));
          break;
        // No confirmation, deliberately. This is the clerical half of the pair:
        // it grants nothing the player could not grant themselves by opening
        // the link already sitting in their inbox, and an admin working through
        // a list of stuck signups should not answer a dialog per row.
        case 'verify-email':
          void act('Email marked verified.', () => verifyUserEmail(id));
          break;
        // This one DOES confirm. Approving identity verification is a
        // compliance statement, and for an account that never submitted it is
        // an override of a review that did not happen — the dialog is where
        // that distinction gets said out loud.
        case 'verify-kyc': {
          const pending = el.dataset.pending === 'true';
          ctx.confirm({
            title: 'Approve identity verification?',
            body: pending
              ? 'This account has a submission waiting. Approving it here is the same decision the KYC tab makes, and the player gets the approval email.'
              : 'This account never submitted anything to review. It will be marked APPROVED anyway, recorded in its activity as an override, and the player is NOT emailed.',
            confirmLabel: 'Approve',
            onConfirm: () => void act('KYC approved.', () => verifyUserKyc(id)),
          });
          break;
        }
        case 'ban':
          ctx.confirm({
            title: 'Ban this account?',
            body: 'The player is signed out everywhere immediately and cannot sign back in.',
            confirmLabel: 'Ban',
            onConfirm: () => void act('Account banned.', () => banUser(id)),
          });
          break;
        case 'close-detail':
          detail = null;
          events = [];
          paint();
          break;
        case 'delete':
          if (!detail) break;
          ctx.confirm({
            title: `Delete ${detail.username || detail.email}?`,
            body: 'This cascades: tokens, ledger, quests, chat, items, invoices and withdrawals all go with it. It cannot be undone.',
            confirmLabel: 'Delete permanently',
            onConfirm: () => {
              const id = detail!.id;
              detail = null;
              void act('Account deleted.', () => deleteUser(id));
            },
          });
          break;
        default:
          break;
      }
    };

    const onSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      if (!form.dataset.form) return;

      if (form.dataset.form === 'create') {
        e.preventDefault();
        const v = readForm(form);
        const email = String(v.email || '').trim();
        const password = String(v.password || '');
        if (!email.includes('@')) { ctx.notify('Enter a valid email address.', 'err'); return; }
        if (password.length < 8) { ctx.notify('Password must be at least 8 characters.', 'err'); return; }
        creating = false;
        void act('Player created.', () => createUser({
          email,
          username: String(v.username || '').trim() || undefined,
          password,
          role: v.role as PlayerRole,
          emailVerified: Boolean(v.emailVerified),
        }));
        return;
      }

      if (!detail) return;
      e.preventDefault();
      const values = readForm(form);
      const id = detail.id;

      if (form.dataset.form === 'edit') {
        const username = String(values.username || '').trim();
        const email = String(values.email || '').trim();
        if (username.length < 3) { ctx.notify('Username must be at least 3 characters.', 'err'); return; }
        void act('Player updated.', () => updatePlayer(id, { username, email, role: values.role as PlayerRole }));
        return;
      }

      if (form.dataset.form === 'credits') {
        const amount = Number(values.amount);
        // The endpoint takes @IsInt() @IsPositive(); catching it here means a
        // typo says what is wrong instead of returning a validation array.
        if (!Number.isInteger(amount) || amount <= 0) {
          ctx.notify('Amount must be a whole number above zero.', 'err');
          return;
        }
        const reason = String(values.reason || '').trim();
        if (reason.length < 3) {
          ctx.notify('A reason is required for a grant.', 'err');
          return;
        }
        void act(`Granted ${amount}.`, () => grantCredits(id, {
          amount,
          currency: values.currency as CurrencyCode,
          reason,
          idempotencyKey: String(values.idempotencyKey || ''),
        }));
        return;
      }

      if (form.dataset.form === 'password') {
        const password = String(values.password || '');
        if (password.length < 8) {
          ctx.notify('Password must be at least 8 characters.', 'err');
          return;
        }
        ctx.confirm({
          title: 'Set a new password?',
          body: 'Every session for this account ends, and any outstanding reset link stops working.',
          confirmLabel: 'Set password',
          onConfirm: () => void act('Password set.', () => setUserPassword(id, password)),
        });
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.target !== searchInput) return;
      e.preventDefault();
      query = {
        page: 1,
        search: searchInput.value.trim() || undefined,
        status: statusSelect.value as UsersQuery['status'],
      };
      void load();
    };

    host.addEventListener('click', onClick);
    host.addEventListener('submit', onSubmit as EventListener);
    host.addEventListener('keydown', onKey);

    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('submit', onSubmit as EventListener);
      host.removeEventListener('keydown', onKey);
    };
  },
};
